import type {
  CordonNode,
  Segment,
  Alert,
  PendingArrival,
  TimeSeriesPoint,
  LogEventItem,
  CameraInfo,
  ActiveTab,
  HoldingComplex,
  HoldingRoom
} from '../types';
import {
  CONFIG,
  NODES,
  ORIGINS,
  INITIAL_SEGMENTS,
  ORDER,
  TRAINS,
  LAG,
  QLINES,
  QSTAGES,
  STAGE_ACTIVE_LINES,
  XSTAGES,
  SECTORS,
  HOLDING_COMPLEXES,
  isOut
} from '../config/crowdConfig';
import {
  fmt,
  hhmm,
  rnd,
  throughput,
  segmentDensity,
  losLabel,
  cellArea,
  xArea,
  qKey
} from '../utils/crowdMath';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import baseapi from '../../baseapi';
import { readLast, saveLast } from '../../utils/lastAnswer';

interface QueueState {
  hold: number;
  holdOpen: boolean;
  holdCap: number;
  cells: Record<string, number>;
  xcells: Record<string, number>;
  darshanToday: number;
  cams: CameraInfo[];
}

interface CrowdContextType {
  clock: number;
  running: boolean;
  speed: number;
  inside: number;
  total: number;
  peak: number;
  rateIn: number;
  rateOut: number;
  darshanRate: number;
  queue: number;
  series: TimeSeriesPoint[];
  srcTotals: Record<string, number>;
  srcRate: Record<string, number>;
  nodeTot: Record<string, number>;
  nodeRate: Record<string, number>;
  alerts: Alert[];
  congested: string | null;
  muted: Set<string>;
  events: LogEventItem[];
  segments: Segment[];
  nodes: Record<string, CordonNode>;
  queueState: QueueState;
  holdingComplexes: HoldingComplex[];
  activeTab: ActiveTab;
  isFullScreen: boolean;
  isRightRailOpen: boolean;
  activeCameraModal: CameraInfo | null;
  activeEntry: string;
  layerState: Record<string, boolean>;
  mapTypeId: string;
  pingedNode: string | null;
  lastArrivals: number;
  pending: PendingArrival[];
  themeMode: 'dark' | 'light';

  // Actions
  toggleThemeMode: () => void;
  setThemeMode: (mode: 'dark' | 'light') => void;
  tick: () => void;
  trainArrival: (auto?: boolean) => void;
  busBatch: (auto?: boolean) => void;
  forceSurge: () => void;
  togglePause: () => void;
  changeSpeed: () => void;
  toggleHoldRelease: () => void;
  toggleHoldingAreaGate: (areaId: string) => void;
  updateRoomGate: (areaId: string, roomId: string, status: 'open' | 'metered' | 'closed') => void;
  flushHoldingRoom: (areaId: string, roomId: string) => void;
  toggleRoomAmenity: (areaId: string, roomId: string, amenity: 'coolingFans' | 'waterAvailable' | 'medicalPost') => void;
  toggleLaneMute: (id: string) => void;
  toggleSegmentShut: (id: string) => void;
  setActiveTab: (tab: ActiveTab) => void;
  toggleFullScreen: (val?: boolean) => void;
  toggleRightRail: (val?: boolean) => void;
  openCamera: (camIdOrObj: string | Partial<CameraInfo>) => void;
  closeCamera: () => void;
  setActiveEntry: (entry: string) => void;
  toggleLayer: (layerKey: string) => void;
  setMapTypeId: (type: string) => void;
  logEvent: (kind: LogEventItem['kind'], html: string) => void;
  updateNodePosition: (id: string, lat: number, lng: number) => void;
  updateSegmentData: (id: string, updates: Partial<Segment>) => void;
  arrivedToday: number;
  darshanTotalToday: number;
  releaseRateVal: number;
}

const CrowdContext = createContext<CrowdContextType | null>(null);

// Build initial cameras
const buildInitialCameras = (): CameraInfo[] => {
  const cams: CameraInfo[] = [];
  QLINES.forEach(l => QSTAGES.forEach(st => {
    cams.push({
      id: `CAM-${st.sector.replace("S-", "S")}-${l.id}`,
      stage: st.id,
      line: l.id,
      sector: st.sector,
      place: st.name,
      kind: "queue",
      stream: null
    });
  }));
  XSTAGES.forEach(st => {
    for (let i = 1; i <= st.cams; i++) {
      cams.push({
        id: `CAM-${st.sector.replace("S-", "S")}-X${i}`,
        stage: st.id,
        line: null,
        sector: st.sector,
        place: st.name,
        kind: "exit",
        stream: null
      });
    }
  });
  ["S-22", "S-24", "S-25", "S-28"].forEach((sc) => {
    cams.push({
      id: `CAM-${sc.replace("S-", "S")}-A1`,
      stage: null,
      line: null,
      sector: sc,
      place: (SECTORS.find(x => x.c === sc) || {}).n || sc,
      kind: "area",
      stream: null
    });
  });
  // Holding Areas Cameras
  HOLDING_COMPLEXES.forEach(ha => {
    ha.rooms.forEach(rm => {
      rm.cams.forEach(camId => {
        cams.push({
          id: camId,
          stage: null,
          line: null,
          sector: ha.sector,
          place: `${ha.name} · ${rm.name}`,
          kind: "holding",
          stream: null,
          holdingAreaId: ha.id,
          roomId: rm.id,
          roomCount: rm.currentCount,
          roomCap: rm.capacity
        });
      });
    });
  });
  // Sector S-17 Prasadam & Laddu Distribution Counters (12 counters)
  for (let i = 1; i <= 12; i++) {
    const pad = String(i).padStart(2, '0');
    cams.push({
      id: `CAM-S17-C${pad}`,
      stage: "XS6",
      line: null,
      sector: "S-17",
      place: i === 12
        ? "Prasadam Counter 12 (Physically Handicapped Priority Stall)"
        : `Prasadam & Laddu Distribution Stall ${i}`,
      kind: "area",
      stream: null
    });
  }
  return cams;
};

const getISTMinutes = (): number => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 3600000 * 5.5);
  return ist.getHours() * 60 + ist.getMinutes();
};

// Pure release rate calculation
const calcReleaseRate = (q: QueueState, lastArr: number): number => {
  if (!q.holdOpen) return 0;
  const intake = QLINES.filter(l => !l.service)
    .reduce((a, l) => a + cellArea(l.id, QSTAGES[0]) * 4.2 * 0.10, 0);
  const load = QSTAGES.reduce((a, st) => a + QLINES.reduce((b, l) => b + (q.cells[qKey(l.id, st.id)] || 0), 0), 0);
  const easing = load > 20000 ? 0.35 : load > 13000 ? 0.7 : 1;
  return Math.min(intake * easing, (lastArr || 280) * 1.05 + q.hold * 0.045);
};

// Pure queue tick
const tickQueuesHelper = (arrivals: number, q: QueueState): { darshan: number; dispersal: number; newQ: QueueState } => {
  let hold = Math.min(q.holdCap, q.hold + arrivals);
  const release = q.holdOpen ? Math.min(hold, calcReleaseRate(q, arrivals)) : 0;
  hold -= release;

  const QWALK = 26;
  const XWALK = 44;
  let darshanNow = 0;
  let dispersal = 0;
  let darshanToday = q.darshanToday;
  const nextCells = { ...q.cells };
  const nextXCells = { ...q.xcells };

  QLINES.forEach(l => {
    if (l.service) return;
    for (let i = QSTAGES.length - 1; i >= 0; i--) {
      const st = QSTAGES[i];
      const isActive = STAGE_ACTIVE_LINES[st.id]?.includes(l.id);
      const k = qKey(l.id, st.id);
      if (!isActive) {
        nextCells[k] = 0;
        continue;
      }
      const here = nextCells[k] || 0;
      if (st.darshan) {
        const served = Math.min(here, l.rate * rnd(0.92, 1.08));
        nextCells[k] = here - served;
        darshanToday += served;
        darshanNow += served;
        continue;
      }
      let nxtIndex = -1;
      for (let j = i + 1; j < QSTAGES.length; j++) {
        if (STAGE_ACTIVE_LINES[QSTAGES[j].id]?.includes(l.id)) {
          nxtIndex = j;
          break;
        }
      }
      if (nxtIndex !== -1) {
        const nxt = QSTAGES[nxtIndex];
        const nk = qKey(l.id, nxt.id);
        const room = Math.max(0, cellArea(l.id, nxt) * 4.2 - (nextCells[nk] || 0));
        const reachable = here * Math.min(1, QWALK / st.len);
        const move = Math.min(here, reachable, room);
        nextCells[k] = here - move;
        nextCells[nk] = (nextCells[nk] || 0) + move;
      }
    }
    const firstActiveStage = QSTAGES.find(st => STAGE_ACTIVE_LINES[st.id]?.includes(l.id));
    if (firstActiveStage) {
      const k0 = qKey(l.id, firstActiveStage.id);
      const cap0 = cellArea(l.id, firstActiveStage) * 4.2;
      const want = release * l.share;
      const took = Math.min(want, Math.max(0, cap0 - (nextCells[k0] || 0)));
      nextCells[k0] = (nextCells[k0] || 0) + took;
      hold += (want - took);
    }
  });

  for (let i = XSTAGES.length - 1; i >= 0; i--) {
    const st = XSTAGES[i];
    const here = nextXCells[st.id] || 0;
    if (i === XSTAGES.length - 1) {
      dispersal = Math.min(here, here * Math.min(1, XWALK / st.len));
      nextXCells[st.id] = here - dispersal;
      continue;
    }
    const nxt = XSTAGES[i + 1];
    const room = Math.max(0, xArea(nxt) * 3.6 - (nextXCells[nxt.id] || 0));
    const move = Math.min(here, here * Math.min(1, XWALK / st.len), room);
    nextXCells[st.id] = here - move;
    nextXCells[nxt.id] = (nextXCells[nxt.id] || 0) + move;
  }
  nextXCells[XSTAGES[0].id] = (nextXCells[XSTAGES[0].id] || 0) + darshanNow;

  return {
    darshan: darshanNow,
    dispersal,
    newQ: {
      ...q,
      hold,
      cells: nextCells,
      xcells: nextXCells,
      darshanToday
    }
  };
};

// Pure network propagation
const propagateHelper = (
  curSrcRate: Record<string, number>,
  rOut: number,
  segs: Segment[],
  congestedId: string | null
) => {
  const nf: Record<string, number> = {};
  ORDER.forEach(id => { nf[id] = 0; });
  ORIGINS.forEach(o => { nf[o.id] = curSrcRate[o.id] || 0; });

  const updatedSegs = segs.map(s => ({ ...s }));
  updatedSegs.forEach(s => {
    if (!isOut(s)) {
      s.vol = 0;
      s.served = 0;
    }
  });

  ORDER.forEach(id => {
    if (id === "TEMPLE") return;
    const outs = updatedSegs.filter(s => s.from === id && !isOut(s) && s.open !== false);
    if (!outs.length) return;
    const denom = outs.reduce((a, s) => a + (s.share || 1), 0);
    outs.forEach(s => {
      s.vol = nf[id] * ((s.share || 1) / denom);
      s.served = throughput(s, congestedId);
      nf[s.to] += s.served;
    });
  });

  const x1 = updatedSegs.find(s => s.id === "X1");
  const x2 = updatedSegs.find(s => s.id === "X2");
  if (x1) { x1.vol = rOut; x1.served = throughput(x1, congestedId); }
  if (x2) { x2.vol = rOut; x2.served = throughput(x2, congestedId); }
  nf["EXIT-KDN"] = x1 ? (x1.served || 0) : rOut;
  nf["KDNAGAR"] = x2 ? (x2.served || 0) : rOut;
  nf["TEMPLE"] = updatedSegs.filter(s => s.to === "TEMPLE").reduce((a, s) => a + (s.served || 0), 0);

  updatedSegs.forEach(s => {
    s.dens = segmentDensity(s);
  });

  return { nf, updatedSegs };
};

// Pure alert evaluation
const evaluateAlertsHelper = (
  curInside: number,
  curRateIn: number,
  curRateOut: number,
  curPending: PendingArrival[],
  curSegs: Segment[]
): Alert[] => {
  const A: Alert[] = [];
  const occ = curInside / CONFIG.CAPACITY;
  if (occ > 0.92) {
    A.push({
      lvl: "crit",
      ttl: "Occupancy critical",
      ms: `${fmt(curInside)} inside against a ${fmt(CONFIG.CAPACITY)} holding capacity.`,
      act: "Hold the Q-line at Bhavani Ghat · clear the exit to Kanaka Durga Nagar"
    });
  } else if (occ > 0.80) {
    A.push({
      lvl: "warn",
      ttl: "Occupancy high",
      ms: `Occupancy at ${Math.round(occ * 100)}%. Net inflow ${fmt(curRateIn - curRateOut)}/min.`,
      act: "Stage the next batch at Boddu Bomma"
    });
  }

  if (curRateIn - curRateOut > 130) {
    A.push({
      lvl: "warn",
      ttl: "Inflow outpacing exit",
      ms: `+${fmt(curRateIn - curRateOut)} per minute net. The exit is the constraint, not the entrance.`,
      act: "Widen the Kanaka Durga Nagar exit lane"
    });
  }

  const jam = curSegs.filter(s => s.open !== false && (s.backlog || 0) > 400)
    .sort((a, b) => (b.backlog || 0) - (a.backlog || 0))[0];
  if (jam) {
    const back = Math.round((jam.backlog || 0) / (jam.width * 4));
    A.push({
      lvl: "crit",
      ttl: "Bottleneck forming",
      ms: `${NODES[jam.from]?.name || jam.from} → ${NODES[jam.to]?.name || jam.to} can pass ${fmt(jam.cap || 0)}/min but is being offered ${fmt(jam.vol || 0)}/min. ${fmt(jam.backlog || 0)} standing, line extending roughly ${back} m back.`,
      act: jam.kind === "queue"
        ? "Open the second Q-line · meter release from Boddu Bomma"
        : "Hold the upstream cordon until the line clears"
    });
  }

  const worst = curSegs.filter(s => s.open !== false).sort((a, b) => (b.dens || 0) - (a.dens || 0))[0];
  if (worst && (worst.dens || 0) > 3 && (!jam || worst.id !== jam.id)) {
    A.push({
      lvl: (worst.dens || 0) > 4 ? "crit" : "warn",
      ttl: "Corridor density",
      ms: `${NODES[worst.from]?.name || worst.from} → ${NODES[worst.to]?.name || worst.to} at ${(worst.dens || 0).toFixed(1)} p/m² across ${worst.width} m — ${losLabel(worst.dens || 0)}.`,
      act: worst.kind === "queue"
        ? "Meter release from Boddu Bomma · open the second Q-line"
        : "Hold upstream cordon · deploy reserve platoon"
    });
  }

  const pendingTotal = curPending.reduce((a, p) => a + p.n, 0);
  if (pendingTotal > 2400) {
    A.push({
      lvl: "warn",
      ttl: "Large batch inbound",
      ms: `${fmt(pendingTotal)} devotees in transit from the station and bus stand.`,
      act: "Pre-position at Gadde Bomma"
    });
  }
  return A;
};

// Compute rich realistic initial state on load
const computeInitialCrowdState = () => {
  const initClock = getISTMinutes();
  const h = (initClock / 60) % 24;
  const shape = 0.45 + 0.85 * Math.exp(-Math.pow((h - 6.5) / 2.1, 2))
    + 1.0 * Math.exp(-Math.pow((h - 18.5) / 2.6, 2))
    + 0.35 * Math.exp(-Math.pow((h - 11) / 2.4, 2));

  const totalIn = Math.max(0, 268 * shape + rnd(-10, 10));
  let inNow = 0;
  const initSrcRate: Record<string, number> = {};
  const initSrcTotals: Record<string, number> = {};
  ORIGINS.forEach(o => {
    const n = Math.round(totalIn * o.share * rnd(0.92, 1.08));
    initSrcRate[o.id] = n;
    initSrcTotals[o.id] = Math.round(CONFIG.START_TOTAL * o.share);
    inNow += n;
  });

  const cells: Record<string, number> = {};
  const xcells: Record<string, number> = {};
  QLINES.forEach(l => QSTAGES.forEach(st => {
    const isActive = STAGE_ACTIVE_LINES[st.id]?.includes(l.id);
    cells[qKey(l.id, st.id)] = isActive ? Math.round(rnd(60, 190)) : 0;
  }));
  XSTAGES.forEach(st => {
    xcells[st.id] = Math.round(rnd(60, 240));
  });
  // Dedicated Mouna Muni Exit 2 count
  xcells['XS_MM'] = Math.round(rnd(80, 210));

  const initQueueState: QueueState = {
    hold: 3200,
    holdOpen: true,
    holdCap: 12000,
    cells,
    xcells,
    darshanToday: 0,
    cams: buildInitialCameras()
  };

  const initHoldingComplexes: HoldingComplex[] = HOLDING_COMPLEXES.map(ha => ({
    ...ha,
    rooms: ha.rooms.map(rm => ({ ...rm }))
  }));

  const initialSegs = INITIAL_SEGMENTS.map(s => ({ ...s }));
  const { nf: initNodeRate, updatedSegs: initSegments } = propagateHelper(initSrcRate, 265, initialSegs, null);

  const initNodeTot: Record<string, number> = {};
  ORIGINS.forEach(o => {
    initNodeTot[o.id] = Math.round(CONFIG.START_TOTAL * o.share);
  });
  initNodeTot["GADDE"] = (initNodeTot["STN-MAIN"] || 0) + (initNodeTot["STN-THARA"] || 0) + (initNodeTot["STN-ET2"] || 0) + (initNodeTot["STN-PARCEL"] || 0);
  initNodeTot["VINAYAKA"] = initNodeTot["GADDE"] + (initNodeTot["PNBS"] || 0);
  initNodeTot["BODDU"] = initNodeTot["VINAYAKA"] + (initNodeTot["LOCAL"] || 0);
  initNodeTot["VIPGATE"] = Math.round(initNodeTot["BODDU"] * 0.03);
  initNodeTot["QLINE"] = Math.round(initNodeTot["BODDU"] * 0.97);
  initNodeTot["TEMPLE"] = Math.round(initNodeTot["QLINE"] - 4200);
  initNodeTot["EXIT-KDN"] = Math.round(initNodeTot["TEMPLE"] - 3800);
  initNodeTot["KDNAGAR"] = initNodeTot["EXIT-KDN"];

  const initSeries: TimeSeriesPoint[] = [];
  for (let i = 0; i < 90; i++) {
    initSeries.push({
      in: 280 + Math.random() * 60,
      out: 270 + Math.random() * 60,
      inside: 6800 + Math.random() * 900
    });
  }

  return {
    initClock,
    initSrcRate,
    initSrcTotals,
    initRateIn: inNow,
    initRateOut: 265,
    initQueueState,
    initHoldingComplexes,
    initSegments,
    initNodeRate,
    initNodeTot,
    initSeries
  };
};

export const CrowdProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initStateRef = useRef<ReturnType<typeof computeInitialCrowdState>>(null!);
  if (!initStateRef.current) {
    initStateRef.current = computeInitialCrowdState();
  }
  const init = initStateRef.current;

  // React state hooks
  const [clock, setClock] = useState<number>(init.initClock);
  const [running, setRunning] = useState<boolean>(true);
  const [speed, setSpeed] = useState<number>(1);
  const [inside, setInside] = useState<number>(8240);
  const [total, setTotal] = useState<number>(CONFIG.START_TOTAL);
  const [peak, setPeak] = useState<number>(12180);
  const [rateIn, setRateIn] = useState<number>(init.initRateIn);
  const [rateOut, setRateOut] = useState<number>(init.initRateOut);
  const [darshanRate, setDarshanRate] = useState<number>(230);
  const [queue, setQueue] = useState<number>(3100);
  const [base] = useState<number>(268);
  const [, setSurge] = useState<number>(0);
  const [congested, setCongested] = useState<string | null>(null);
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const [lastArrivals, setLastArrivals] = useState<number>(init.initRateIn);
  const [pending, setPending] = useState<PendingArrival[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('flow');
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isRightRailOpen, setIsRightRailOpen] = useState<boolean>(false);
  const [activeCameraModal, setActiveCameraModal] = useState<CameraInfo | null>(null);
  const [activeEntry, setActiveEntry] = useState<string>('all');
  const [mapTypeId, setMapTypeId] = useState<string>('roadmap');
  const [pingedNode, setPingedNode] = useState<string | null>(null);
  const [themeMode, setThemeModeState] = useState<'dark' | 'light'>(() => {
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains('dark');
      setThemeModeState(isDark ? 'dark' : 'light');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const setThemeMode = useCallback((mode: 'dark' | 'light') => {
    setThemeModeState(mode);
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleThemeMode = useCallback(() => {
    const isDark = document.documentElement.classList.contains('dark');
    if (isDark) {
      document.documentElement.classList.remove('dark');
      setThemeModeState('light');
    } else {
      document.documentElement.classList.add('dark');
      setThemeModeState('dark');
    }
  }, []);

  const [layerState, setLayerState] = useState<Record<string, boolean>>({
    boundary: true, approach: true, rail: true, bus: true, holding: true, bridges: true,
    incidents: true, in: true, out: true, cams: true, gates: true, heat: true
  });

  const [nodes, setNodes] = useState<Record<string, CordonNode>>(() => ({ ...NODES }));
  const [segments, setSegments] = useState<Segment[]>(init.initSegments);
  const [srcTotals, setSrcTotals] = useState<Record<string, number>>(init.initSrcTotals);
  const [srcRate, setSrcRate] = useState<Record<string, number>>(init.initSrcRate);
  const [nodeTot, setNodeTot] = useState<Record<string, number>>(init.initNodeTot);
  const [nodeRate, setNodeRate] = useState<Record<string, number>>(init.initNodeRate);
  const [series, setSeries] = useState<TimeSeriesPoint[]>(init.initSeries);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [events, setEvents] = useState<LogEventItem[]>(() => [
    { id: '1', time: hhmm(CONFIG.START_CLOCK), kind: 'sys', html: 'Console armed against the surveyed cordon network.' },
    { id: '2', time: hhmm(CONFIG.START_CLOCK), kind: 'gate', html: `Baseline — <b>${fmt(8240)}</b> devotees on premises at shift handover.` }
  ]);
  const [queueState, setQueueState] = useState<QueueState>(init.initQueueState);
  const [holdingComplexes, setHoldingComplexes] = useState<HoldingComplex[]>(init.initHoldingComplexes);

  // Sync refs for the simulation loop to avoid race conditions and stale closures
  const clockRef = useRef(init.initClock);
  const surgeRef = useRef(0);
  const pendingRef = useRef<PendingArrival[]>([]);
  const srcTotalsRef = useRef(init.initSrcTotals);
  const nodeTotRef = useRef(init.initNodeTot);
  const queueStateRef = useRef(init.initQueueState);
  const holdingComplexesRef = useRef(init.initHoldingComplexes);
  const segmentsRef = useRef(init.initSegments);
  const congestedRef = useRef<string | null>(null);
  const seriesRef = useRef<TimeSeriesPoint[]>(init.initSeries);

  // Event logger
  const logEvent = useCallback((kind: LogEventItem['kind'], html: string) => {
    const newItem: LogEventItem = {
      id: `${Date.now()}-${Math.random()}`,
      time: hhmm(clockRef.current),
      kind,
      html
    };
    setEvents(prev => [newItem, ...prev.slice(0, 40)]);
  }, []);

  // Ping node on map
  const pingNode = useCallback((id: string) => {
    setPingedNode(id);
    setTimeout(() => setPingedNode(null), 3000);
  }, []);

  const releaseRateVal = calcReleaseRate(queueState, lastArrivals);

  // Trigger train arrival
  const trainArrival = useCallback((auto = false) => {
    const t = TRAINS[Math.floor(Math.random() * TRAINS.length)];
    const alighted = Math.round(rnd(780, 1650));
    const frac = rnd(0.52, 0.71);
    const bound = Math.round(alighted * frac);
    const split: Record<string, number> = { "STN-MAIN": 0.38, "STN-THARA": 0.28, "STN-ET2": 0.22, "STN-PARCEL": 0.12 };
    const curClock = clockRef.current;

    const newArrivals: PendingArrival[] = [];
    Object.keys(split).forEach(id => {
      const [a, b] = LAG[id] || [10, 15];
      newArrivals.push({
        nodeId: id,
        n: bound * split[id],
        eta: curClock + Math.round(rnd(a, b)),
        spread: Math.round(rnd(7, 12))
      });
    });

    pendingRef.current = [...pendingRef.current, ...newArrivals];
    setPending(pendingRef.current);

    logEvent(
      "rail",
      `<b>${t.no} ${t.name}</b> from ${t.from} berthed at PF-${Math.ceil(rnd(1, 10))}. Platform cameras counted <b>${fmt(alighted)}</b> alighting; <b>${fmt(bound)}</b> turned toward Indrakeeladri (${Math.round(frac * 100)}%). First arrivals at Gadde Bomma from ${hhmm(curClock + 12)}.`
    );

    pingNode("STN-MAIN");
    if (!auto) {
      surgeRef.current += 40;
      setSurge(surgeRef.current);
    }
  }, [logEvent, pingNode]);

  // Trigger bus batch
  const busBatch = useCallback((auto = false) => {
    const buses = Math.round(rnd(6, 18));
    const heads = Math.round(buses * rnd(38, 54));
    const curClock = clockRef.current;
    const [a, b] = LAG["PNBS"] || [10, 15];

    const newArrival: PendingArrival = {
      nodeId: "PNBS",
      n: heads,
      eta: curClock + Math.round(rnd(a, b)),
      spread: Math.round(rnd(5, 9))
    };

    pendingRef.current = [...pendingRef.current, newArrival];
    setPending(pendingRef.current);

    logEvent("bus", `<b>${buses} APSRTC services</b> cleared PNBS bays · <b>${fmt(heads)}</b> devotees counted at the exit gantry, routed to Vinayaka Temple junction.`);

    pingNode("PNBS");
    if (!auto) {
      surgeRef.current += 25;
      setSurge(surgeRef.current);
    }
  }, [logEvent, pingNode]);

  const forceSurge = useCallback(() => {
    surgeRef.current += 420;
    setSurge(surgeRef.current);
    const qSeg = segmentsRef.current.find(s => s.id === "Q1");
    congestedRef.current = qSeg ? "Q1" : null;
    setCongested(congestedRef.current);
    logEvent(
      "alert",
      `Incident on the <b>Bhavani Ghat queue line</b> — usable width lost, throughput cut to ${fmt((qSeg ? (qSeg.cap || 300) : 300) * 0.42)}/min against ${fmt(qSeg ? (qSeg.vol || 0) : 0)}/min arriving.`
    );
    setTimeout(() => {
      congestedRef.current = null;
      setCongested(null);
    }, 45000);
  }, [logEvent]);

  const togglePause = useCallback(() => {
    setRunning(r => !r);
  }, []);

  const changeSpeed = useCallback(() => {
    setSpeed(sp => (sp === 1 ? 4 : sp === 4 ? 12 : 1));
  }, []);

  const toggleHoldRelease = useCallback(() => {
    const nextHoldOpen = !queueStateRef.current.holdOpen;
    queueStateRef.current = { ...queueStateRef.current, holdOpen: nextHoldOpen };
    setQueueState(queueStateRef.current);
    logEvent(
      nextHoldOpen ? "gate" : "alert",
      nextHoldOpen
        ? "Holding area <b>released</b> — devotees moving through the S-23 corridor into the Q-lines."
        : "Holding area <b>closed</b> at S-22. Devotees are being held; the Q-lines will start to drain."
    );
  }, [logEvent]);

  const toggleHoldingAreaGate = useCallback((areaId: string) => {
    const updated: HoldingComplex[] = holdingComplexesRef.current.map(ha => {
      if (ha.id !== areaId) return ha;
      const nextGatesOpen = !ha.gatesOpen;
      const nextStatus: 'open' | 'closed' = nextGatesOpen ? 'open' : 'closed';
      logEvent(
        nextGatesOpen ? "gate" : "alert",
        `<b>${ha.name}</b> gates set to <b>${nextStatus.toUpperCase()}</b>.`
      );
      return {
        ...ha,
        gatesOpen: nextGatesOpen,
        rooms: ha.rooms.map(rm => ({
          ...rm,
          gateStatus: nextStatus
        }))
      };
    });
    holdingComplexesRef.current = updated;
    setHoldingComplexes(updated);
  }, [logEvent]);

  const updateRoomGate = useCallback((areaId: string, roomId: string, status: 'open' | 'metered' | 'closed') => {
    const updated: HoldingComplex[] = holdingComplexesRef.current.map(ha => {
      if (ha.id !== areaId) return ha;
      return {
        ...ha,
        rooms: ha.rooms.map(rm => {
          if (rm.id !== roomId) return rm;
          logEvent("gate", `<b>${ha.name}</b> · ${rm.name} gate changed to <b>${status.toUpperCase()}</b>.`);
          return { ...rm, gateStatus: status };
        })
      };
    });
    holdingComplexesRef.current = updated;
    setHoldingComplexes(updated);
  }, [logEvent]);

  const flushHoldingRoom = useCallback((areaId: string, roomId: string) => {
    const updated: HoldingComplex[] = holdingComplexesRef.current.map(ha => {
      if (ha.id !== areaId) return ha;
      return {
        ...ha,
        rooms: ha.rooms.map(rm => {
          if (rm.id !== roomId) return rm;
          logEvent("alert", `⚡ <b>FLUSH INITIATED</b> on ${ha.name} · ${rm.name}. Rapid evacuation towards downstream release corridor.`);
          return {
            ...rm,
            gateStatus: 'open' as const,
            currentCount: Math.max(0, Math.round(rm.currentCount * 0.25)),
            status: 'normal' as const
          };
        })
      };
    });
    holdingComplexesRef.current = updated;
    setHoldingComplexes(updated);
  }, [logEvent]);

  const toggleRoomAmenity = useCallback((areaId: string, roomId: string, amenity: 'coolingFans' | 'waterAvailable' | 'medicalPost') => {
    const updated = holdingComplexesRef.current.map(ha => {
      if (ha.id !== areaId) return ha;
      return {
        ...ha,
        rooms: ha.rooms.map(rm => {
          if (rm.id !== roomId) return rm;
          const nextVal = !rm[amenity];
          const labelMap: Record<string, string> = {
            coolingFans: "Cooling mist fans",
            waterAvailable: "RO drinking water supply",
            medicalPost: "Emergency medical desk"
          };
          logEvent("sys", `<b>${rm.name}</b> ${labelMap[amenity]} status set to <b>${nextVal ? "ENABLED" : "OFFLINE"}</b>.`);
          return { ...rm, [amenity]: nextVal };
        })
      };
    });
    holdingComplexesRef.current = updated;
    setHoldingComplexes(updated);
  }, [logEvent]);

  const toggleLaneMute = useCallback((id: string) => {
    setMuted(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSegmentShut = useCallback((id: string) => {
    const target = segmentsRef.current.find(s => s.id === id);
    if (!target) return;
    const willOpen = target.open === false;
    const fromNode = nodes[target.from]?.name || target.from;
    const toNode = nodes[target.to]?.name || target.to;
    const sib = segmentsRef.current.filter(x => x.from === target.from && x.id !== id && x.open !== false)
      .map(x => nodes[x.to]?.name || x.to);

    logEvent(
      !willOpen ? "alert" : "gate",
      !willOpen
        ? `<b>${fromNode} → ${toNode}</b> closed. ${sib.length ? "Flow diverted via " + sib.join(" and ") + "." : "No alternative from this cordon — crowd will hold upstream."}`
        : `<b>${fromNode} → ${toNode}</b> reopened.`
    );

    const updated = segmentsRef.current.map(s => s.id === id ? { ...s, open: willOpen } : s);
    segmentsRef.current = updated;
    setSegments(updated);
  }, [logEvent, nodes]);

  const toggleLayer = useCallback((layerKey: string) => {
    setLayerState(prev => ({ ...prev, [layerKey]: !prev[layerKey] }));
  }, []);

  const openCamera = useCallback((camIdOrObj: string | Partial<CameraInfo>) => {
    if (typeof camIdOrObj === 'object' && camIdOrObj !== null && camIdOrObj.id) {
      const fullCam: CameraInfo = {
        id: camIdOrObj.id,
        sector: camIdOrObj.sector || "S-4",
        place: camIdOrObj.place || "Kummaripalem Jn -> Tollgate",
        stage: camIdOrObj.stage ?? null,
        line: camIdOrObj.line ?? null,
        kind: camIdOrObj.kind || 'area',
        stream: camIdOrObj.stream ?? null,
      };
      setActiveCameraModal(fullCam);
      return;
    }
    const camId = typeof camIdOrObj === 'string' ? camIdOrObj : "CAM-S4-L1";
    const c = queueStateRef.current.cams.find(cam => cam.id === camId) || (
      camId.startsWith('CAM-S17-')
        ? {
            id: camId,
            sector: "S-17",
            place: "Prasadam & Laddu Counters",
            stage: "XS6",
            line: null,
            kind: "area" as const,
            stream: null
          }
        : {
            id: camId,
            sector: "S-4",
            place: "Kummaripalem Jn -> Tollgate",
            stage: "QS1",
            line: "L1",
            kind: "queue" as const,
            stream: null
          }
    );
    setActiveCameraModal(c);
  }, []);

  const closeCamera = useCallback(() => {
    setActiveCameraModal(null);
  }, []);

  const toggleFullScreen = useCallback((val?: boolean) => {
    setIsFullScreen(prev => {
      const next = typeof val === 'boolean' ? val : !prev;
      if (next && document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
      if (!next && document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
      return next;
    });
  }, []);

  const updateNodePosition = useCallback((id: string, lat: number, lng: number) => {
    setNodes(prev => {
      const target = prev[id];
      if (!target) return prev;
      logEvent("sys", `<b>${target.name}</b> moved to ${lat.toFixed(5)}, ${lng.toFixed(5)} — adjoining segments recalculated.`);
      return {
        ...prev,
        [id]: { ...target, lat, lng, moved: true }
      };
    });
  }, [logEvent]);

  const updateSegmentData = useCallback((id: string, updates: Partial<Segment>) => {
    const updated = segmentsRef.current.map(s => s.id === id ? { ...s, ...updates } : s);
    segmentsRef.current = updated;
    setSegments(updated);
  }, []);

  // Main Simulation Tick (pure sequential execution using refs)
  const tick = useCallback(() => {
    const nextClock = clockRef.current + 1;
    clockRef.current = nextClock;

    const h = (nextClock / 60) % 24;
    const shape = 0.45 + 0.85 * Math.exp(-Math.pow((h - 6.5) / 2.1, 2))
      + 1.0 * Math.exp(-Math.pow((h - 18.5) / 2.6, 2))
      + 0.35 * Math.exp(-Math.pow((h - 11) / 2.4, 2));

    surgeRef.current *= 0.93;

    const arrivedNow: Record<string, number> = {};
    const nextPending = pendingRef.current.filter(p => {
      if (nextClock >= p.eta) {
        arrivedNow[p.nodeId] = (arrivedNow[p.nodeId] || 0) + p.n / p.spread;
        p.n -= p.n / p.spread;
        p.spread--;
        p.eta += 1;
        return p.spread > 0;
      }
      return true;
    });
    pendingRef.current = nextPending;

    const totalIn = Math.max(0, base * shape + surgeRef.current + rnd(-25, 25));
    let inNow = 0;
    const nextSrcRate: Record<string, number> = {};
    const nextSrcTotals = { ...srcTotalsRef.current };
    ORIGINS.forEach(o => {
      const n = totalIn * o.share * rnd(0.85, 1.15) + (arrivedNow[o.id] || 0);
      nextSrcRate[o.id] = n;
      nextSrcTotals[o.id] = (nextSrcTotals[o.id] || 0) + n;
      inNow += n;
    });
    srcTotalsRef.current = nextSrcTotals;

    const qr = tickQueuesHelper(inNow, queueStateRef.current);
    queueStateRef.current = qr.newQ;
    const newDarshan = qr.darshan;
    const newDispersal = qr.dispersal;

    const qWait = QSTAGES.slice(0, 4).reduce(
      (a, st) => a + QLINES.reduce((b, l) => b + (qr.newQ.cells[qKey(l.id, st.id)] || 0), 0),
      0
    );
    const preDarsh = QSTAGES.slice(4).reduce(
      (a, st) => a + QLINES.reduce((b, l) => b + (qr.newQ.cells[qKey(l.id, st.id)] || 0), 0),
      0
    );
    const currentInside = preDarsh + XSTAGES.reduce((a, st) => a + (qr.newQ.xcells[st.id] || 0), 0);

    // Holding complexes simulation
    const nextHoldingComplexes: HoldingComplex[] = holdingComplexesRef.current.map(ha => {
      const rooms: HoldingRoom[] = ha.rooms.map(rm => {
        let infStep = 0;
        let outfStep = 0;

        if (rm.gateStatus === 'open') {
          if (rm.currentCount < rm.capacity) {
            infStep = Math.random() < 0.65 ? Math.floor(rnd(1, 3.5)) : 0;
          }
          if (rm.currentCount > 0) {
            outfStep = Math.random() < 0.60 ? Math.floor(rnd(1, 3.2)) : 0;
          }
        } else if (rm.gateStatus === 'metered') {
          if (rm.currentCount < rm.capacity) {
            infStep = Math.random() < 0.45 ? Math.floor(rnd(1, 2.5)) : 0;
          }
          if (rm.currentCount > 0) {
            outfStep = Math.random() < 0.45 ? Math.floor(rnd(1, 2.5)) : 0;
          }
        } else {
          infStep = 0;
          if (rm.currentCount > 0) {
            outfStep = Math.random() < 0.4 ? 1 : 0;
          }
        }

        const nextCount = Math.max(0, Math.min(rm.capacity, rm.currentCount + infStep - outfStep));
        const occRatio = nextCount / rm.capacity;

        const dispInflow = Math.round(infStep * 16 + (rm.gateStatus === 'closed' ? 0 : rnd(6, 18)));
        const dispOutflow = Math.round(outfStep * 16 + (nextCount === 0 ? 0 : rnd(5, 16)));

        const status: 'normal' | 'dense' | 'critical' | 'closed' =
          rm.gateStatus === 'closed' && nextCount === 0
            ? 'closed'
            : occRatio >= 0.90
            ? 'critical'
            : occRatio >= 0.70
            ? 'dense'
            : 'normal';

        return {
          ...rm,
          currentCount: nextCount,
          inflowRate: dispInflow,
          outflowRate: dispOutflow,
          status
        };
      });

      return { ...ha, rooms };
    });
    holdingComplexesRef.current = nextHoldingComplexes;

    // Trigger train and bus schedules periodically
    if (Math.random() < 0.028) trainArrival(true);
    if (Math.random() < 0.045) busBatch(true);

    // Propagate network flow
    const { nf, updatedSegs } = propagateHelper(nextSrcRate, newDispersal, segmentsRef.current, congestedRef.current);
    segmentsRef.current = updatedSegs;

    const nextNodeTot = { ...nodeTotRef.current };
    ORDER.forEach(id => {
      nextNodeTot[id] = (nextNodeTot[id] || 0) + (nf[id] || 0);
    });
    nodeTotRef.current = nextNodeTot;

    const newAlerts = evaluateAlertsHelper(currentInside, inNow, newDispersal, nextPending, updatedSegs);

    const nextSeries = [...seriesRef.current, { in: inNow, out: newDispersal, inside: currentInside }];
    if (nextSeries.length > 90) nextSeries.shift();
    seriesRef.current = nextSeries;

    // React state dispatches
    setClock(nextClock);
    setRateIn(inNow);
    setRateOut(newDispersal);
    setDarshanRate(newDarshan);
    setQueue(qWait);
    setInside(currentInside);
    setPeak(p => Math.max(p, currentInside));
    setLastArrivals(inNow);
    setTotal(t => t + inNow);
    setPending(nextPending);
    setSrcTotals(nextSrcTotals);
    setSrcRate(nextSrcRate);
    setQueueState(qr.newQ);
    setHoldingComplexes(nextHoldingComplexes);
    setSegments(updatedSegs);
    setNodeRate(nf);
    setNodeTot(nextNodeTot);
    setAlerts(newAlerts);
    setSeries(nextSeries);
  }, [base, trainArrival, busBatch]);

  // Interval loop
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => {
      tickRef.current();
    }, 1600 / speed);
    return () => clearInterval(iv);
  }, [running, speed]);

  // Derived totals
  const arrivedToday = CONFIG.START_TOTAL + queueState.darshanToday + queueState.hold + queue +
    QSTAGES.slice(4).reduce((a, st) => a + QLINES.reduce((b, l) => b + (queueState.cells[qKey(l.id, st.id)] || 0), 0), 0);
  const darshanTotalToday = CONFIG.START_TOTAL + queueState.darshanToday;

  const toggleRightRail = useCallback((val?: boolean) => {
    setIsRightRailOpen(prev => (val !== undefined ? val : !prev));
  }, []);

  // ---- live holding areas: GET /api/holding every 5 s. Once the server answers, every box shows what its
  // camera measures (people now, in/out per minute over the last 15 min); a box without a camera gets 0 and
  // live=false so screens show "—" instead of a simulated number. If the API is not there, the simulation stays.
  const [holdingApi, setHoldingApi] = useState<any | null>(() => readLast<any>('holding'));   // opens on the last known counts
  useEffect(() => {
    let alive = true;
    const load = () => baseapi.get('holding')
      .then(r => { if (alive && r.data && Array.isArray(r.data.areas)) { setHoldingApi(r.data); saveLast('holding', r.data); } })
      .catch(() => { /* keep the last answer */ });
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const liveHoldingComplexes = useMemo<HoldingComplex[]>(() => {
    if (!holdingApi) return holdingComplexes;
    const boxes = new Map<string, any>();
    for (const a of holdingApi.areas) for (const b of a.boxes || []) boxes.set(b.id, b);
    const dense = holdingApi.thresholds?.dense ?? 0.7, critical = holdingApi.thresholds?.critical ?? 0.9;
    return holdingComplexes.map(ha => {
      const area = holdingApi.areas.find((a: any) => a.id === ha.id);
      if (!area) return ha;
      const rooms = ha.rooms.map(rm => {
        const b = boxes.get(rm.id);
        const live = !!b && b.source === 'camera' && b.count != null;
        const count = live ? b.count : 0;
        const ratio = count / Math.max(1, rm.capacity);
        return {
          ...rm, live, currentCount: count,
          cams: b && b.cameras?.length ? b.cameras.map((c: any) => (typeof c === 'string' ? c : c.id)) : rm.cams,
          inflowRate: live && b.entered_15m != null ? Math.round(b.entered_15m / 15) : 0,
          outflowRate: live && b.exited_15m != null ? Math.round(b.exited_15m / 15) : 0,
          status: rm.status === 'closed' ? rm.status : ratio >= critical ? 'critical' as const : ratio >= dense ? 'dense' as const : 'normal' as const,
        };
      });
      const exitCounting = (area.exit_cameras || []).some((c: any) => c.state === 'online' || c.state === 'replay');
      return { ...ha, rooms, cctvCount: area.boxes_with_camera ?? 0,
        releasedPerMin: exitCounting && area.released_15m != null ? area.released_15m / 15 : null };
    });
  }, [holdingApi, holdingComplexes]);

  return (
    <CrowdContext.Provider
      value={{
        clock,
        running,
        speed,
        inside,
        total,
        peak,
        rateIn,
        rateOut,
        darshanRate,
        queue,
        series,
        srcTotals,
        srcRate,
        nodeTot,
        nodeRate,
        alerts,
        congested,
        muted,
        events,
        segments,
        nodes,
        queueState,
        holdingComplexes: liveHoldingComplexes,
        activeTab,
        isFullScreen,
        isRightRailOpen,
        activeCameraModal,
        activeEntry,
        layerState,
        mapTypeId,
        pingedNode,
        lastArrivals,
        pending,
        themeMode,

        toggleThemeMode,
        setThemeMode,
        tick,
        trainArrival,
        busBatch,
        forceSurge,
        togglePause,
        changeSpeed,
        toggleHoldRelease,
        toggleHoldingAreaGate,
        updateRoomGate,
        flushHoldingRoom,
        toggleRoomAmenity,
        toggleLaneMute,
        toggleSegmentShut,
        setActiveTab,
        toggleFullScreen,
        toggleRightRail,
        openCamera,
        closeCamera,
        setActiveEntry,
        toggleLayer,
        setMapTypeId,
        logEvent,
        updateNodePosition,
        updateSegmentData,
        arrivedToday,
        darshanTotalToday,
        releaseRateVal
      }}
    >
      {children}
    </CrowdContext.Provider>
  );
};

export const useCrowd = (): CrowdContextType => {
  const context = useContext(CrowdContext);
  if (!context) {
    throw new Error('useCrowd must be used within a CrowdProvider');
  }
  return context;
};
